import type { QueryClient } from '@tanstack/react-query';

import config from '@/config';
import { USER_ROLE } from '@/constants';
import { AUTH_KEY } from '@shared/auth';
import { defaultQueryClient } from '@shared/query-client';

/**
 * Router context (improvements/08-routing-tanstack-router.md, step 1):
 * `{ queryClient, auth, config }` handed to `createRouter({ context })` once,
 * in the shared factory, so every `loader`/`beforeLoad` in the catalog reads
 * them from `ctx` instead of reaching for module singletons or hitting
 * `localStorage` directly from a route module.
 *
 * `auth` is deliberately a pair of *functions*, not booleans: the context
 * object is built once at router-creation time, while auth state changes
 * during the session (login via `/callback`, logout, token refresh). Reading
 * lazily inside a `beforeLoad` gives the current value; a snapshotted boolean
 * would be the value from app boot, which is the exact bug
 * `shared/auth/auth-state.ts` was written to fix on the render side.
 */
export interface RouterAuth {
	/** Cognito `AUTH_KEY` flag -- the same source `useIsAuthenticated` reads. */
	isAuthenticated: () => boolean;
	isAdmin: () => boolean;
}

export interface RouterContext {
	queryClient: QueryClient;
	auth: RouterAuth;
	config: typeof config;
}

export const routerAuth: RouterAuth = {
	isAuthenticated: () => localStorage.getItem(AUTH_KEY) === 'true',
	isAdmin: () => localStorage.getItem(USER_ROLE) === 'admin',
};

export function createRouterContext(): RouterContext {
	return {
		queryClient: defaultQueryClient,
		auth: routerAuth,
		config,
	};
}
